import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";

const TIER_STYLES = {
    A: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    B: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    C: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

export default function Stores() {
    const [rows, setRows] = useState([]);
    const [region, setRegion] = useState("All");

    useEffect(() => {
        http.get("/masters/stores").then((r) => setRows(r.data));
    }, []);

    const regions = useMemo(() => Array.from(new Set(rows.map((r) => r.region))), [rows]);
    const filtered = useMemo(
        () => (region === "All" ? rows : rows.filter((r) => r.region === region)),
        [rows, region]
    );

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Master Data · Stores</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Multi-Brand Outlet Network</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">{filtered.length} stores</div>
            </div>

            <div className="flex items-center gap-1 rounded-sm border border-border bg-card p-0.5 w-fit">
                {["All", ...regions].map((r) => (
                    <button
                        key={r}
                        onClick={() => setRegion(r)}
                        data-testid={`stores-region-${r}`}
                        className={
                            "rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors " +
                            (region === r
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {r}
                    </button>
                ))}
            </div>

            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="stores-table">
                <div className="max-h-[calc(100vh-260px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">Code</th>
                                <th className="px-3 py-2 text-left font-semibold">Name</th>
                                <th className="px-3 py-2 text-left font-semibold">City</th>
                                <th className="px-3 py-2 text-left font-semibold">Region</th>
                                <th className="px-3 py-2 text-left font-semibold">Channel</th>
                                <th className="px-3 py-2 text-left font-semibold">Tier</th>
                                <th className="px-3 py-2 text-right font-semibold">Health</th>
                                <th className="px-3 py-2 text-right font-semibold">Geo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`store-row-${r.id}`}
                                    className="border-t border-border hover:bg-muted/40"
                                >
                                    <td className="mono-num px-3 py-1.5 font-medium">{r.code}</td>
                                    <td className="px-3 py-1.5">{r.name}</td>
                                    <td className="px-3 py-1.5">{r.city}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{r.region}</td>
                                    <td className="px-3 py-1.5">{r.channel}</td>
                                    <td className="px-3 py-1.5">
                                        <span
                                            className={
                                                "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                TIER_STYLES[r.tier]
                                            }
                                        >
                                            {r.tier}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <div className="h-1 w-20 overflow-hidden rounded-full bg-border">
                                                <div
                                                    className={
                                                        "h-full " +
                                                        (r.health_score >= 80
                                                            ? "bg-emerald-500"
                                                            : r.health_score >= 60
                                                            ? "bg-primary"
                                                            : "bg-red-400")
                                                    }
                                                    style={{ width: `${r.health_score}%` }}
                                                />
                                            </div>
                                            <span className="mono-num text-[11px] font-semibold">
                                                {r.health_score}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="mono-num px-3 py-1.5 text-right text-muted-foreground">
                                        {r.lat.toFixed(2)}, {r.lng.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
